import type { FC, FormEvent, ReactNode } from 'react'
import type { FieldErrors, LoginInput, SignupInput } from '@/lib/validation'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { focusFirstError, postJson } from '@/lib/submit'
import { validateLogin, validateSignup } from '@/lib/validation'
import { Button } from '@/components/generals/button'
import { PLAN_OPTIONS } from '@/constants/forms'

import {
    CheckboxField,
    FormAlert,
    PasswordField,
    SelectField,
    TextField
} from '@/components/forms/fields'

/** Only allows redirects back into this site after signing in. */
const safeNext = (value: unknown): string => {
    if (typeof value !== 'string' || !value.startsWith('/')) return '/app'

    try {
        const base = 'https://veyra.local'
        const url = new URL(value, base)

        if (url.origin === base) return `${url.pathname}${url.search}${url.hash}`
    }
    catch {
        return '/app'
    }

    return '/app'
}

export const LoginForm: FC = (): ReactNode => {
    const router = useRouter()
    const [values, setValues] = useState<LoginInput>({ email: '', password: '' })
    const [errors, setErrors] = useState<FieldErrors<keyof LoginInput>>({})
    const [message, setMessage] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)

    const update = (field: keyof LoginInput, value: string) => {
        setValues(current => ({ ...current, [field]: value }))

        if (errors[field]) setErrors(current => ({ ...current, [field]: undefined }))
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setMessage(null)

        const found = validateLogin({ email: values.email.trim(), password: values.password })

        if (Object.keys(found).length) {
            setErrors(found)
            focusFirstError('login', (['email', 'password'] as const).filter(field => found[field]))

            return
        }

        setSubmitting(true)

        const result = await postJson('/api/auth/login', { email: values.email.trim(), password: values.password })

        if (result.ok) {
            await router.push(safeNext(router.query.next))

            return
        }

        setSubmitting(false)
        setMessage(result.message ?? null)

        if (result.errors) setErrors(result.errors as FieldErrors<keyof LoginInput>)
    }

    return (
        <form noValidate onSubmit={onSubmit} className='space-y-6'>
            {message && <FormAlert tone='error'>{message}</FormAlert>}

            <TextField
                id='login-email'
                label='Work email'
                type='email'
                inputMode='email'
                autoComplete='email'
                value={values.email}
                onChange={event => update('email', event.target.value)}
                error={errors.email}
            />

            <PasswordField
                id='login-password'
                label='Password'
                autoComplete='current-password'
                value={values.password}
                onChange={event => update('password', event.target.value)}
                error={errors.password}
            />

            <Button type='submit' size='lg' arrow loading={submitting} className='w-full'>
                {submitting ? 'Signing in' : 'Log in'}
            </Button>

            <p className='text-center text-sm text-stone-dark'>
                New to Veyra?{' '}
                <Link href='/signup' className='font-medium text-burgundy underline-offset-4 hover:underline'>
                    Create an account
                </Link>
            </p>
        </form>
    )
}

export const SignupForm: FC = (): ReactNode => {
    const router = useRouter()
    const [values, setValues] = useState<SignupInput>({ name: '', email: '', company: '', password: '', plan: 'growth', agree: false })
    const [errors, setErrors] = useState<FieldErrors<keyof SignupInput>>({})
    const [message, setMessage] = useState<string | null>(null)
    const [exists, setExists] = useState(false)
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
        if (!router.isReady) return

        const plan = typeof router.query.plan === 'string' ? router.query.plan.toLowerCase() : ''

        if (PLAN_OPTIONS.some(option => option.value === plan)) setValues(current => ({ ...current, plan }))
    }, [router.isReady, router.query.plan])

    const update = <K extends keyof SignupInput>(field: K, value: SignupInput[K]) => {
        setValues(current => ({ ...current, [field]: value }))

        if (errors[field]) setErrors(current => ({ ...current, [field]: undefined }))
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setMessage(null)
        setExists(false)

        const payload = { ...values, name: values.name.trim(), email: values.email.trim(), company: values.company.trim() }
        const found = validateSignup(payload)

        if (Object.keys(found).length) {
            setErrors(found)
            focusFirstError('signup', (['name', 'email', 'company', 'password', 'plan', 'agree'] as const).filter(field => found[field]))

            return
        }

        setSubmitting(true)

        const result = await postJson('/api/auth/signup', payload)

        if (result.ok) {
            await router.push('/app')

            return
        }

        setSubmitting(false)
        setMessage(result.message ?? null)
        setExists(Boolean(result.errors?.email && result.message?.includes('already exists')))

        if (result.errors) setErrors(result.errors as FieldErrors<keyof SignupInput>)
    }

    return (
        <form noValidate onSubmit={onSubmit} className='space-y-6'>
            {message && (
                <FormAlert tone='error'>
                    {message}
                    {exists && (
                        <>
                            {' '}
                            <Link href='/login' className='font-medium underline underline-offset-4'>Go to log in</Link>
                        </>
                    )}
                </FormAlert>
            )}

            <TextField
                id='signup-name'
                label='Full name'
                autoComplete='name'
                value={values.name}
                onChange={event => update('name', event.target.value)}
                error={errors.name}
            />

            <TextField
                id='signup-email'
                label='Work email'
                type='email'
                inputMode='email'
                autoComplete='email'
                value={values.email}
                onChange={event => update('email', event.target.value)}
                error={errors.email}
            />

            <div className='grid gap-6 sm:grid-cols-2'>
                <TextField
                    id='signup-company'
                    label='Company'
                    autoComplete='organization'
                    optional
                    value={values.company}
                    onChange={event => update('company', event.target.value)}
                    error={errors.company}
                />

                <SelectField
                    id='signup-plan'
                    label='Plan'
                    options={PLAN_OPTIONS}
                    value={values.plan}
                    onChange={event => update('plan', event.target.value)}
                    error={errors.plan}
                />
            </div>

            <PasswordField
                id='signup-password'
                label='Password'
                autoComplete='new-password'
                value={values.password}
                onChange={event => update('password', event.target.value)}
                error={errors.password}
                hint='At least 8 characters.'
            />

            <CheckboxField
                id='signup-agree'
                checked={values.agree}
                onChange={checked => update('agree', checked)}
                error={errors.agree}
            >
                I agree to the{' '}
                <Link href='/terms' className='font-medium text-burgundy underline underline-offset-4'>Terms of service</Link>
                {' '}and{' '}
                <Link href='/privacy' className='font-medium text-burgundy underline underline-offset-4'>Privacy policy</Link>.
            </CheckboxField>

            <Button type='submit' size='lg' arrow loading={submitting} className='w-full'>
                {submitting ? 'Creating your workspace' : 'Create account'}
            </Button>

            <p className='text-center text-sm text-stone-dark'>
                Already have an account?{' '}
                <Link href='/login' className='font-medium text-burgundy underline-offset-4 hover:underline'>
                    Log in
                </Link>
            </p>
        </form>
    )
}
